import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/context/AuthContext';
import { getKPIs, createKPI, updateKPI, deleteKPI, getKPIAnalytics, getKPITrends, exportKPIAnalytics, importKPIs, getAutomatedKPIs } from '@/services/api';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import html2canvas from 'html2canvas';
import { PageTabs } from '@/components/PageTabs';
import { BarChart3, Target } from 'lucide-react';

const HUB_TABS = [
  { path: '/kpis', label: 'KPIs', icon: BarChart3 },
  { path: '/goals', label: 'Goals & OKRs', icon: Target },
];
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, 
  LineChart, Line, PieChart, Pie, Cell, Area, AreaChart, Legend
} from 'recharts';
import {
  Plus,
  Filter,
  Trash2,
  TrendingUp,
  TrendingDown,
  Minus,
  AlertTriangle,
  CheckCircle2,
  Activity,
  PieChart as PieChartIcon,
  Edit,
  RefreshCw,
  Download,
  Camera,
  FileSpreadsheet,
  Mail,
  Info,
  Upload,
  Zap
} from 'lucide-react';

const industries = [
  { value: 'logistics', label: 'Logistics' },
  { value: 'manufacturing', label: 'Manufacturing' },
  { value: 'healthcare', label: 'Healthcare' },
  { value: 'retail', label: 'Retail' },
];

const categories = [
  { value: 'quality', label: 'Quality', color: '#2372D4' },
  { value: 'delivery', label: 'Delivery', color: '#10b981' },
  { value: 'cost', label: 'Cost', color: '#f59e0b' },
  { value: 'safety', label: 'Safety', color: '#ef4444' },
  { value: 'morale', label: 'Morale', color: '#8b5cf6' },
  { value: 'productivity', label: 'Productivity', color: '#00CED1' },
];

const COLORS = ['#00CED1', '#2372D4', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];

const getTrendIcon = (trend) => {
  switch (trend) {
    case 'up': return <TrendingUp className="h-4 w-4 text-emerald-500" />;
    case 'down': return <TrendingDown className="h-4 w-4 text-rose-500" />;
    default: return <Minus className="h-4 w-4 text-muted-foreground" />;
  }
};

const getPerformanceColor = (value, target) => {
  const ratio = value / target;
  if (ratio >= 1) return 'text-emerald-600';
  if (ratio >= 0.8) return 'text-amber-600';
  return 'text-rose-600';
};

const getPerformanceBg = (performance) => {
  if (performance >= 100) return 'bg-emerald-50 border-emerald-200';
  if (performance >= 80) return 'bg-amber-50 border-amber-200';
  return 'bg-rose-50 border-rose-200';
};

import { perfColor as perfColorCls } from '@/utils/styleHelpers';
import { KPIOverviewTab } from './kpis/KPIOverviewTab';
import { KPIPlatformTab } from './kpis/KPIPlatformTab';
import { KPITrendsTab } from './kpis/KPITrendsTab';
import { KPIMetricsTab } from './kpis/KPIMetricsTab';

export const KPIsPage = () => {
  const { isAdmin, isMasterAdmin, isGuest } = useAuth();
  const [kpis, setKpis] = useState([]);
  const [automatedKpis, setAutomatedKpis] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [trends, setTrends] = useState([]);
  const [loading, setLoading] = useState(true);
  const [industryFilter, setIndustryFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingKPI, setEditingKPI] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [exporting, setExporting] = useState(false);
  const [dateRange, setDateRange] = useState({ from: '', to: '' });
  const [inlineEdit, setInlineEdit] = useState(null); // { metricId, field, value }
  const [importLoading, setImportLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const dashboardRef = useRef(null);
  const fileInputRef = useRef(null);
  const [newKPI, setNewKPI] = useState({
    name: '',
    category: 'quality',
    value: 0,
    target: 100,
    unit: '%',
    industry: 'logistics'
  });

  useEffect(() => {
    loadData();
  }, [industryFilter, categoryFilter]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [kpisData, analyticsData, trendsData, autoData] = await Promise.all([
        getKPIs(
          industryFilter !== 'all' ? industryFilter : null,
          categoryFilter !== 'all' ? categoryFilter : null
        ),
        getKPIAnalytics(industryFilter !== 'all' ? industryFilter : null),
        getKPITrends(
          30,
          industryFilter !== 'all' ? industryFilter : null,
          dateRange.from || null,
          dateRange.to || null
        ),
        getAutomatedKPIs()
      ]);
      setKpis(kpisData);
      setAnalytics(analyticsData);
      setTrends(trendsData);
      setAutomatedKpis(autoData?.automated_kpis || []);
    } catch (error) {
      toast.error('Failed to load KPI data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateKPI = async () => {
    try {
      const kpi = await createKPI(newKPI);
      setKpis([kpi, ...kpis]);
      setDialogOpen(false);
      setNewKPI({
        name: '',
        category: 'quality',
        value: 0,
        target: 100,
        unit: '%',
        industry: 'logistics'
      });
      loadData(); // Refresh analytics
      toast.success('KPI created');
    } catch (error) {
      toast.error('Failed to create KPI');
    }
  };

  const handleUpdateKPI = async () => {
    if (!editingKPI) return;
    try {
      await updateKPI(editingKPI.metric_id, {
        name: editingKPI.name,
        category: editingKPI.category,
        value: editingKPI.value,
        target: editingKPI.target,
        unit: editingKPI.unit,
        industry: editingKPI.industry,
        trend: editingKPI.trend
      });
      setEditingKPI(null);
      loadData();
      toast.success('KPI updated');
    } catch (error) {
      toast.error('Failed to update KPI');
    }
  };

  const handleDeleteKPI = async (metricId) => {
    if (!window.confirm('Delete this KPI?')) return;
    try {
      await deleteKPI(metricId);
      setKpis(kpis.filter(k => k.metric_id !== metricId));
      loadData();
      toast.success('KPI deleted');
    } catch (error) {
      toast.error('Failed to delete KPI');
    }
  };

  const handleInlineSave = async (metricId, field, newValue) => {
    const kpi = kpis.find(k => k.metric_id === metricId);
    if (!kpi) return;
    const numVal = parseFloat(newValue);
    if (isNaN(numVal)) { setInlineEdit(null); return; }
    if (numVal === kpi[field]) { setInlineEdit(null); return; }
    try {
      await updateKPI(metricId, { ...kpi, [field]: numVal });
      setInlineEdit(null);
      loadData();
      toast.success('KPI updated');
    } catch (error) {
      toast.error(error.message || 'Failed to update');
      setInlineEdit(null);
    }
  };

  const handleImportExcel = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportLoading(true);
    try {
      const result = await importKPIs(file);
      toast.success(`Imported ${result.count} KPIs`);
      loadData();
    } catch (error) {
      toast.error(error.message || 'Import failed');
    } finally {
      setImportLoading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const mailtoExport = (filename) => {
    const subject = encodeURIComponent(`KAIZEN KANRI - ${filename}`);
    const body = encodeURIComponent(`Hi,\n\nPlease find the attached export: ${filename}\n\nGenerated from KAIZEN KANRI KPI Dashboard.\n\nBest regards`);
    window.open(`mailto:?subject=${subject}&body=${body}`);
  };

  const handleScreenshot = async () => {
    if (!dashboardRef.current) return;
    
    try {
      const canvas = await html2canvas(dashboardRef.current, {
        backgroundColor: '#ffffff',
        scale: 2
      });
      
      const link = document.createElement('a');
      link.download = `kpi-dashboard-${new Date().toISOString().split('T')[0]}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      
      toast.success('Dashboard screenshot saved');
    } catch (error) {
      toast.error('Failed to capture screenshot');
    }
  };

  const handleExportAnalytics = async () => {
    setExporting(true);
    try {
      const data = await exportKPIAnalytics('excel');
      
      if (data instanceof Blob) {
        const url = window.URL.createObjectURL(data);
        const a = document.createElement('a');
        a.href = url;
        a.download = `kpi-analytics-${new Date().toISOString().split('T')[0]}.xlsx`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }
      
      toast.success('KPI Analytics exported');
    } catch (error) {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  const handleEmailShare = () => {
    const subject = encodeURIComponent('KAIZEN KANRI - KPI Dashboard');
    const body = encodeURIComponent(`Please find the KPI Dashboard summary:\n\nTotal KPIs: ${analytics?.total_kpis || 0}\nAverage Performance: ${analytics?.avg_performance?.toFixed(1) || 0}%\nAbove Target: ${analytics?.above_target || 0}\nBelow Target: ${analytics?.below_target || 0}`);
    window.open(`mailto:?subject=${subject}&body=${body}`, '_blank');
    toast.info('Email client opened');
  };

  // Filter KPIs by status card selection (thresholds match backend: on_target = 0.9-1.1, below = <0.9)
  const filteredKpis = statusFilter === 'all'
    ? kpis
    : statusFilter === 'on_target'
      ? kpis.filter(k => {
          const ratio = k.target > 0 ? k.value / k.target : 0;
          return ratio >= 0.9;
        })
      : kpis.filter(k => {
          const ratio = k.target > 0 ? k.value / k.target : 0;
          return ratio < 0.9;
        });

  const statusFilterLabel = statusFilter === 'all'
    ? 'All KPIs'
    : statusFilter === 'on_target'
      ? 'On Target KPIs'
      : 'Needs Attention KPIs';

  // Prepare chart data
  const categoryChartData = analytics?.by_category 
    ? Object.entries(analytics.by_category).map(([cat, data]) => ({
        name: cat.charAt(0).toUpperCase() + cat.slice(1),
        performance: data.avg_performance,
        count: data.count,
        fill: categories.find(c => c.value === cat)?.color || '#64748b'
      }))
    : [];

  const trendStatusData = analytics?.by_trend
    ? [
        { name: 'Improving', value: analytics.by_trend.up, fill: '#10b981' },
        { name: 'Stable', value: analytics.by_trend.stable, fill: '#64748b' },
        { name: 'Declining', value: analytics.by_trend.down, fill: '#ef4444' }
      ].filter(d => d.value > 0)
    : [];

  const performanceDistribution = [
    { name: 'Above Target', value: analytics?.above_target || 0, fill: '#10b981' },
    { name: 'On Target', value: analytics?.on_target || 0, fill: '#3b82f6' },
    { name: 'Below Target', value: analytics?.below_target || 0, fill: '#ef4444' }
  ].filter(d => d.value > 0);

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="kpis-page" ref={dashboardRef}>
        <PageTabs tabs={HUB_TABS} testIdPrefix="kpis-tab" />
        {/* Demo Data Notice for Guests */}
        {isGuest && (
          <div className="flex items-center gap-3 px-4 py-3 bg-sky-50 border border-sky-200 rounded-lg" data-testid="demo-data-banner">
            <Info className="h-5 w-5 text-sky-600 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-sky-800">Sample Data Preview</p>
              <p className="text-xs text-sky-600">The metrics below are illustrative examples and do not represent actual company performance data.</p>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">KPI Analytics</h1>
            <p className="text-muted-foreground font-body mt-1">
              Track performance metrics and trends
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            {!isGuest && (
              <>
                <Button variant="outline" size="sm" onClick={handleScreenshot} title="Save as PNG" data-testid="kpi-screenshot-btn">
                  <Camera className="h-4 w-4 mr-2" /> Screenshot
                </Button>
                <Button variant="outline" size="sm" onClick={handleExportAnalytics} disabled={exporting} title="Export to Excel" data-testid="kpi-export-btn">
                  <FileSpreadsheet className="h-4 w-4 mr-2" /> {exporting ? 'Exporting...' : 'Export'}
                </Button>
                <Button variant="outline" size="sm" onClick={() => mailtoExport('kpi-analytics.xlsx')} title="Share via Email" data-testid="kpi-email-btn">
                  <Mail className="h-4 w-4 mr-2" /> Email
                </Button>
                {isAdmin && (
                  <>
                    <input
                      type="file"
                      ref={fileInputRef}
                      accept=".xlsx,.xls"
                      onChange={handleImportExcel}
                      className="hidden"
                      data-testid="kpi-import-input"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={importLoading}
                      title="Import KPIs from Excel"
                      data-testid="kpi-import-btn"
                    >
                      <Upload className="h-4 w-4 mr-2" /> {importLoading ? 'Importing...' : 'Import'}
                    </Button>
                  </>
                )}
              </>
            )}
            <Button variant="outline" onClick={loadData} data-testid="refresh-btn">
              <RefreshCw className="h-4 w-4 mr-2" /> Refresh
            </Button>
            {!isGuest && (
              <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogTrigger asChild>
                  <Button data-testid="new-kpi-btn">
                    <Plus className="h-4 w-4 mr-2" /> Add KPI
                  </Button>
                </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Add New KPI</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 pt-4">
                  <div className="space-y-2">
                    <Label>KPI Name</Label>
                    <Input
                      value={newKPI.name}
                      onChange={(e) => setNewKPI({ ...newKPI, name: e.target.value })}
                      placeholder="e.g., On-Time Delivery Rate"
                      data-testid="kpi-name"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Category</Label>
                      <Select value={newKPI.category} onValueChange={(v) => setNewKPI({ ...newKPI, category: v })}>
                        <SelectTrigger data-testid="kpi-category">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {categories.map((cat) => (
                            <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Industry</Label>
                      <Select value={newKPI.industry} onValueChange={(v) => setNewKPI({ ...newKPI, industry: v })}>
                        <SelectTrigger data-testid="kpi-industry">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {industries.map((ind) => (
                            <SelectItem key={ind.value} value={ind.value}>{ind.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>Current Value</Label>
                      <Input
                        type="number"
                        value={newKPI.value}
                        onChange={(e) => setNewKPI({ ...newKPI, value: parseFloat(e.target.value) || 0 })}
                        data-testid="kpi-value"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Target</Label>
                      <Input
                        type="number"
                        value={newKPI.target}
                        onChange={(e) => setNewKPI({ ...newKPI, target: parseFloat(e.target.value) || 0 })}
                        data-testid="kpi-target"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Unit</Label>
                      <Input
                        value={newKPI.unit}
                        onChange={(e) => setNewKPI({ ...newKPI, unit: e.target.value })}
                        placeholder="%"
                        data-testid="kpi-unit"
                      />
                    </div>
                  </div>
                  <Button onClick={handleCreateKPI} className="w-full" disabled={!newKPI.name} data-testid="submit-kpi">
                    Add KPI
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
            )}
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-4">
          <Select value={industryFilter} onValueChange={setIndustryFilter}>
            <SelectTrigger className="w-[180px]" data-testid="filter-industry">
              <Filter className="h-4 w-4 mr-2" />
              <SelectValue placeholder="All Industries" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Industries</SelectItem>
              {industries.map((ind) => (
                <SelectItem key={ind.value} value={ind.value}>{ind.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-[180px]" data-testid="filter-category">
              <Filter className="h-4 w-4 mr-2" />
              <SelectValue placeholder="All Categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {categories.map((cat) => (
                <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <>
            {/* Summary Cards - Clickable Filters */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card
                className={`cursor-pointer transition-all hover:shadow-md ${statusFilter === 'all' ? 'ring-2 ring-blue-500 shadow-md' : ''}`}
                onClick={() => setStatusFilter('all')}
                data-testid="total-kpis-card"
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-sm bg-blue-50 flex items-center justify-center">
                      <BarChart3 className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{analytics?.total_kpis || 0}</p>
                      <p className="text-sm text-muted-foreground">Total KPIs</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card data-testid="avg-performance-card">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-sm bg-emerald-50 flex items-center justify-center">
                      <Target className="h-5 w-5 text-emerald-600" />
                    </div>
                    <div>
                      <p className={`text-2xl font-bold ${perfColorCls(analytics?.avg_performance || 0)}`}>
                        {analytics?.avg_performance || 0}%
                      </p>
                      <p className="text-sm text-muted-foreground">Avg Performance</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card
                className={`cursor-pointer transition-all hover:shadow-md ${statusFilter === 'on_target' ? 'ring-2 ring-green-500 shadow-md' : ''}`}
                onClick={() => setStatusFilter('on_target')}
                data-testid="on-target-card"
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-sm bg-green-50 flex items-center justify-center">
                      <CheckCircle2 className="h-5 w-5 text-green-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-green-600">{(analytics?.on_target || 0) + (analytics?.above_target || 0)}</p>
                      <p className="text-sm text-muted-foreground">On Target</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card
                className={`cursor-pointer transition-all hover:shadow-md ${statusFilter === 'needs_attention' ? 'ring-2 ring-rose-500 shadow-md' : ''}`}
                onClick={() => setStatusFilter('needs_attention')}
                data-testid="needs-attention-card"
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-sm bg-rose-50 flex items-center justify-center">
                      <AlertTriangle className="h-5 w-5 text-rose-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-rose-600">{analytics?.below_target || 0}</p>
                      <p className="text-sm text-muted-foreground">Needs Attention</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Tabs for different views */}
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList>
                <TabsTrigger value="overview" data-testid="tab-overview">
                  <Activity className="h-4 w-4 mr-2" /> Overview
                </TabsTrigger>
                <TabsTrigger value="platform" data-testid="tab-platform">
                  <Zap className="h-4 w-4 mr-2" /> Platform KPIs
                </TabsTrigger>
                <TabsTrigger value="trends" data-testid="tab-trends">
                  <TrendingUp className="h-4 w-4 mr-2" /> Trends
                </TabsTrigger>
                <TabsTrigger value="metrics" data-testid="tab-metrics">
                  <BarChart3 className="h-4 w-4 mr-2" /> All Metrics
                </TabsTrigger>
              </TabsList>

              {/* Overview Tab */}
              <TabsContent value="overview" className="space-y-6 mt-6">
                <KPIOverviewTab
                  categoryChartData={categoryChartData}
                  performanceDistribution={performanceDistribution}
                  analytics={analytics}
                  filteredKpis={filteredKpis}
                  statusFilter={statusFilter}
                  statusFilterLabel={statusFilterLabel}
                  categories={categories}
                  isGuest={isGuest}
                  isAdmin={isAdmin}
                  isMasterAdmin={isMasterAdmin}
                  inlineEdit={inlineEdit}
                  setInlineEdit={setInlineEdit}
                  handleInlineSave={handleInlineSave}
                  setEditingKPI={setEditingKPI}
                  handleDeleteKPI={handleDeleteKPI}
                  setDialogOpen={setDialogOpen}
                  getTrendIcon={getTrendIcon}
                  getPerformanceColor={getPerformanceColor}
                  getPerformanceBg={getPerformanceBg}
                />
              </TabsContent>

              {/* Platform KPIs Tab */}
              <TabsContent value="platform" className="space-y-6 mt-6">
                <KPIPlatformTab automatedKpis={automatedKpis} getTrendIcon={getTrendIcon} />
              </TabsContent>

              {/* Trends Tab */}
              <TabsContent value="trends" className="space-y-6 mt-6">
                <KPITrendsTab
                  trends={trends}
                  dateRange={dateRange}
                  setDateRange={setDateRange}
                  loadData={loadData}
                  analytics={analytics}
                />
              </TabsContent>

              {/* All Metrics Tab */}
              <TabsContent value="metrics" className="space-y-6 mt-6">
                <KPIMetricsTab
                  filteredKpis={filteredKpis}
                  statusFilter={statusFilter}
                  statusFilterLabel={statusFilterLabel}
                  categories={categories}
                  isGuest={isGuest}
                  isAdmin={isAdmin}
                  isMasterAdmin={isMasterAdmin}
                  setEditingKPI={setEditingKPI}
                  handleDeleteKPI={handleDeleteKPI}
                  setDialogOpen={setDialogOpen}
                  getTrendIcon={getTrendIcon}
                  getPerformanceColor={getPerformanceColor}
                  getPerformanceBg={getPerformanceBg}
                />
              </TabsContent>
            </Tabs>

            {/* Edit KPI Dialog */}
            <Dialog open={!!editingKPI} onOpenChange={(open) => !open && setEditingKPI(null)}>
              <DialogContent className="sm:max-w-[480px]">
                <DialogHeader>
                  <DialogTitle data-testid="edit-kpi-dialog-title">Edit KPI</DialogTitle>
                </DialogHeader>
                {editingKPI && (
                  <div className="space-y-4 pt-2">
                    <div className="space-y-2">
                      <Label>KPI Name</Label>
                      <Input
                        value={editingKPI.name}
                        onChange={(e) => setEditingKPI({ ...editingKPI, name: e.target.value })}
                        data-testid="edit-kpi-name"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Category</Label>
                        <Select value={editingKPI.category} onValueChange={(v) => setEditingKPI({ ...editingKPI, category: v })}>
                          <SelectTrigger data-testid="edit-kpi-category">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {categories.map((cat) => (
                              <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Industry</Label>
                        <Select value={editingKPI.industry || 'logistics'} onValueChange={(v) => setEditingKPI({ ...editingKPI, industry: v })}>
                          <SelectTrigger data-testid="edit-kpi-industry">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {industries.map((ind) => (
                              <SelectItem key={ind.value} value={ind.value}>{ind.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label>Current Value</Label>
                        <Input
                          type="number"
                          value={editingKPI.value}
                          onChange={(e) => setEditingKPI({ ...editingKPI, value: parseFloat(e.target.value) || 0 })}
                          data-testid="edit-kpi-value"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Target</Label>
                        <Input
                          type="number"
                          value={editingKPI.target}
                          onChange={(e) => setEditingKPI({ ...editingKPI, target: parseFloat(e.target.value) || 0 })}
                          data-testid="edit-kpi-target"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Unit</Label>
                        <Input
                          value={editingKPI.unit}
                          onChange={(e) => setEditingKPI({ ...editingKPI, unit: e.target.value })}
                          data-testid="edit-kpi-unit"
                        />
                      </div>
                    </div>
                    {editingKPI.target > 0 && (
                      <div className="p-3 rounded-md bg-muted/50">
                        <div className="flex items-center justify-between text-sm mb-1">
                          <span className="text-muted-foreground">Current Performance</span>
                          <span className={`font-semibold ${getPerformanceColor(editingKPI.value, editingKPI.target)}`}>
                            {Math.round((editingKPI.value / editingKPI.target) * 100)}%
                          </span>
                        </div>
                        <Progress value={Math.min((editingKPI.value / editingKPI.target) * 100, 100)} className="h-2" />
                      </div>
                    )}
                    <Button onClick={handleUpdateKPI} className="w-full" disabled={!editingKPI.name} data-testid="submit-edit-kpi">
                      Update KPI
                    </Button>
                  </div>
                )}
              </DialogContent>
            </Dialog>
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

export default KPIsPage;
